import { create } from 'zustand';
import { message } from 'antd';
import apiCore from '../utils/api-core';

const useModuleGroupStore = create((set, get) => ({
    // Data
    loading: false,
    context: 'moduleGroup',
    moduleGroup: null,
    modules: [],
    selectedModuleId: null,
    emailGroups: [],

    // Actions
    fetchModuleGroup: async (id) => {
        set({ loading: true });
        try {
            const res = await apiCore.get(`/api/v3/module-group/${id}`);
            const data = res.data || res;
            set({
                moduleGroup: data,
                modules: data.modules || [],
                loading: false,
            });
            // Fetch email groups if company exists
            if (data.company) {
                get().fetchEmailGroups(data.company.id);
            }
        } catch (error) {
            console.error('Failed to fetch module group:', error);
            set({ loading: false });
            message.error('Failed to load module group');
        }
    },

    fetchEmailGroups: async (companyId) => {
        try {
            const res = await apiCore.get(`/api/v1/company/email-groups/${companyId}`);
            set({ emailGroups: res.data || [] });
        } catch (error) {
            console.error('Failed to fetch email groups:', error);
        }
    },

    updateModuleGroupName: async (name) => {
        const { moduleGroup } = get();
        if (!moduleGroup) return;
        try {
            await apiCore.put(`/api/v3/module-group/${moduleGroup.id}`, { name });
            set((state) => ({
                moduleGroup: { ...state.moduleGroup, name },
            }));
            message.success('Module group name updated');
        } catch (error) {
            console.error(error);
            message.error('Failed to update name');
        }
    },

    updateModuleGroupDescription: async (description) => {
        const { moduleGroup } = get();
        if (!moduleGroup) return;
        try {
            await apiCore.put(`/api/v3/module-group/${moduleGroup.id}`, { description });
            set((state) => ({
                moduleGroup: { ...state.moduleGroup, description },
            }));
            message.success('Description updated');
        } catch (error) {
            console.error(error);
            message.error('Failed to update description');
        }
    },

    updateCompany: async (companyId) => {
        const { moduleGroup, fetchModuleGroup } = get();
        if (!moduleGroup) return;
        try {
            await apiCore.put(`/api/v3/module-group/${moduleGroup.id}`, {
                company_id: companyId || null,
            });
            message.success('Company updated');
            await fetchModuleGroup(moduleGroup.id);
        } catch (error) {
            console.error(error);
            message.error('Failed to update company');
        }
    },

    updateTemplateMeta: async ({ is_template, category }) => {
        const { moduleGroup } = get();
        if (!moduleGroup) return;
        try {
            const nextCategory = is_template ? (category || null) : null;
            await apiCore.put(`/api/v3/module-group/${moduleGroup.id}`, {
                is_template,
                category: nextCategory,
            });
            set((state) => ({
                moduleGroup: { ...state.moduleGroup, is_template, category: nextCategory },
            }));
            message.success('Template settings updated');
        } catch (error) {
            console.error(error);
            message.error('Failed to update template settings');
        }
    },

    saveFlowMeta: async (flowMetaData) => {
        const { moduleGroup } = get();
        if (!moduleGroup) return;
        try {
            await apiCore.put(`/api/v3/module-group/${moduleGroup.id}`, { flow_meta: flowMetaData });
            set((state) => ({
                moduleGroup: { ...state.moduleGroup, flow_meta: flowMetaData },
            }));
            message.success('Canvas layout saved');
        } catch (error) {
            console.error(error);
            message.error('Failed to save layout');
        }
    },

    setStartModule: async (moduleId) => {
        const { moduleGroup, fetchModuleGroup } = get();
        if (!moduleGroup) return;
        try {
            await apiCore.put(`/api/v3/module-group/${moduleGroup.id}`, { module_id: moduleId });
            message.success('Start module updated');
            await fetchModuleGroup(moduleGroup.id);
        } catch (error) {
            console.error(error);
            message.error('Failed to set start module');
        }
    },

    createModule: async (moduleData) => {
        const { moduleGroup, fetchModuleGroup } = get();
        if (!moduleGroup) return;
        try {
            await apiCore.post('/api/v3/module-group/modules', {
                ...moduleData,
                modulegroup_id: moduleGroup.id,
            });
            message.success('Module created');
            await fetchModuleGroup(moduleGroup.id);
        } catch (error) {
            console.error(error);
            message.error('Failed to create module');
        }
    },

    updateModule: async (moduleId, moduleData) => {
        const { moduleGroup, fetchModuleGroup } = get();
        if (!moduleGroup) return;
        try {
            await apiCore.put(`/api/v3/module-group/modules/${moduleId}`, moduleData);
            message.success('Module updated');
            await fetchModuleGroup(moduleGroup.id);
        } catch (error) {
            console.error(error);
            message.error('Failed to update module');
        }
    },

    deleteModule: async (moduleId) => {
        const { moduleGroup, fetchModuleGroup } = get();
        if (!moduleGroup) return;
        try {
            await apiCore.delete(`/api/v3/module-group/modules/${moduleId}`);
            message.success('Module deleted');
            set({ selectedModuleId: null });
            await fetchModuleGroup(moduleGroup.id);
        } catch (error) {
            console.error(error);
            message.error('Failed to delete module');
        }
    },

    createConnection: async (moduleId, goto, filters) => {
        const { moduleGroup, fetchModuleGroup } = get();
        if (!moduleGroup) return;
        try {
            await apiCore.post('/api/v3/module-group/connections', {
                module_id: moduleId,
                goto,
                filters,
            });
            message.success('Connection created');
            await fetchModuleGroup(moduleGroup.id);
        } catch (error) {
            console.error(error);
            message.error('Failed to create connection');
        }
    },

    deleteConnection: async (moduleId, gotoId) => {
        const { moduleGroup, fetchModuleGroup } = get();
        if (!moduleGroup) return;
        try {
            await apiCore.delete(`/api/v3/module-group/connections/${moduleId}/${gotoId}`);
            message.success('Connection deleted');
            await fetchModuleGroup(moduleGroup.id);
        } catch (error) {
            console.error(error);
            message.error('Failed to delete connection');
        }
    },

    createTrigger: async (moduleId, gotoModuleId, triggerType, config) => {
        const { moduleGroup, fetchModuleGroup } = get();
        if (!moduleGroup) return;
        try {
            await apiCore.post('/api/v3/module-group/triggers', {
                module_id: moduleId,
                goto_module_id: gotoModuleId,
                trigger_type: triggerType,
                config,
            });
            message.success('Trigger created');
            await fetchModuleGroup(moduleGroup.id);
        } catch (error) {
            console.error(error);
            message.error('Failed to create trigger');
        }
    },

    updateTrigger: async (triggerId, data) => {
        const { moduleGroup, fetchModuleGroup } = get();
        if (!moduleGroup) return;
        try {
            await apiCore.put(`/api/v3/module-group/triggers/${triggerId}`, data);
            message.success('Trigger updated');
            await fetchModuleGroup(moduleGroup.id);
        } catch (error) {
            console.error(error);
            message.error('Failed to update trigger');
        }
    },

    deleteTrigger: async (triggerId) => {
        const { moduleGroup, fetchModuleGroup } = get();
        if (!moduleGroup) return;
        try {
            await apiCore.delete(`/api/v3/module-group/triggers/${triggerId}`);
            message.success('Trigger deleted');
            await fetchModuleGroup(moduleGroup.id);
        } catch (error) {
            console.error(error);
            message.error('Failed to delete trigger');
        }
    },

    setSelectedModuleId: (id) => set({ selectedModuleId: id }),

    getSelectedModule: () => {
        const { modules, selectedModuleId } = get();
        if (!selectedModuleId) return null;
        return modules.find((m) => m.id === selectedModuleId) || null;
    },
}));

export default useModuleGroupStore;
